import React, { useState, useEffect, useContext } from "react";
import { Form, Input, Checkbox, message } from "antd";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../App";
import "./index.scss";
import { setCookie } from "../../utils/setCookie";
import { do_login, login_sms_code, login_by_mobile } from "../../api/login";
import ConfirmModal from "../other/ConfirmModal";
import { formatDateByYearFull } from "../../utils/dateFullUtils";

const LoginModal = ({ visible, onClose, onSuccess }) => {
  // 使用 ThemeContext 获取当前主题和切换函数
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("account"); // 'account' 或 'phone'
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [countdown, setCountdown] = useState(0);
  // 新增协议勾选状态
  const [agreementChecked, setAgreementChecked] = useState(false);
  // 新增状态管理
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);

  // 处罚弹窗
  const [punishmentModalVisible, setPunishmentModalVisible] = useState(false);
  // 处罚通知内容
  const [punishmentContent, setPunishmentContent] = useState("");
  // 处罚时间
  const [punishmentTime, setPunishmentTime] = useState("");
  // 是否需要填验证图案
  const [needCaptcha, setNeedCaptcha] = useState(false);

  // 同步更新到表单实例
  useEffect(() => {
    form.setFieldsValue({
      username: username,
      password: password,
      phone: phoneNumber,
      code: verificationCode,
    });
  }, [username, password, phoneNumber, verificationCode]);
  useEffect(() => {
    if (activeTab === "account") {
      setNeedCaptcha(shouldRequireCaptcha(username));
    } else {
      setNeedCaptcha(false);
    }
  }, [activeTab, username]);

  // 新增滚动锁定逻辑（封装在组件内部）
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (visible) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [visible]);

  useEffect(() => {
    // 加载验证码脚本（调整为 visible 依赖）
    if (!visible) return;
    if (!(activeTab === "account" && needCaptcha)) {
      return () => {};
    }
    // 定义全局回调
    window.luosimao_captcha_verify = (response) => {
      // 自动生成隐藏域的逻辑
      let input = document.querySelector('input[name="luotest_response"]');
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "luotest_response";
        document.querySelector(".login-modal__form").appendChild(input);
      }
      input.value = response;
    };
    const script = document.createElement("script");
    script.src = "https://captcha.luosimao.com/static/dist/api.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      const input = document.querySelector('input[name="luotest_response"]');
      input?.remove();
      delete window.luosimao_captcha_verify;
    };
  }, [visible, activeTab, needCaptcha]);

  // 批量引入图片
  function importAll(r) {
    return r
      .keys()
      .reduce((acc, key) => ({ ...acc, [key.split("/").pop()]: r(key) }), {});
  }
  const images = importAll(
    require.context("../../assets/images/pub", false, /\.(png|jpe?g|svg)$/)
  );

  // 发送验证码
  const handleSendCode = () => {
    const PHONE_REGEX = /^1[3-9]\d{9}$/;
    if (!phoneNumber || !PHONE_REGEX.test(phoneNumber)) {
      message.warning("请输入有效的手机号码");
      return;
    } else {
      // 这里添加发送验证码的API调用
      login_sms_code({
        roaming_code: "+86",
        mobile: phoneNumber,
      }).then((res) => {
        if (res.data.status === 200) {
          message.success("验证码已发送");
          // 开始倒计时
          setCountdown(120);
          const timer = setInterval(() => {
            setCountdown((prevCount) => {
              if (prevCount <= 1) {
                clearInterval(timer);
                return 0;
              }
              return prevCount - 1;
            });
          }, 1000);
        } else {
          message.warning(res.data.msg);
        }
      });
    }
  };

  // 重置验证码
  const resetCaptcha = () => {
    // 重新加载验证码
    if (window.LUOCAPTCHA && typeof window.LUOCAPTCHA.reset === "function") {
      window.LUOCAPTCHA.reset();
    } else {
      // 如果LUOCAPTCHA对象不存在或没有reset方法，则重新加载脚本
      const captchaContainer = document.querySelector(".l-captcha");
      if (captchaContainer) {
        captchaContainer.innerHTML = "";
        const script = document.createElement("script");
        script.src = "https://captcha.luosimao.com/static/dist/api.js";
        script.async = true;
        document.body.appendChild(script);
      }
    }
  };

  const failUserName = (name) => `login_fail_${name || ""}`;
  const getFailInfo = (name) => {
    if (!name) return;
    try {
      const getLocal = localStorage.getItem(failUserName(name));
      return getLocal ? JSON.parse(getLocal) : null;
    } catch (e) {
      return null;
    }
  };
  // 是否同一天
  const isSameDay = (firstErrorTime, currentTime) => {
    const firstError = new Date(firstErrorTime);
    const current = new Date(currentTime);
    return (
      firstError.getFullYear() === current.getFullYear() &&
      firstError.getMonth() === current.getMonth() &&
      firstError.getDate() === current.getDate()
    );
  };
  const shouldRequireCaptcha = (name) => {
    if (!name) return false;
    const info = getFailInfo(name);
    if (!info) return false;
    if (!isSameDay(info.firstTime, Date.now())) {
      clearFail(name);
      return false;
    }
    return info.count >= 1;
  };
  //记录失败的次数
  // 当天只有第一次登录不用进行验证码校验
  const failCount = (name) => {
    if (!name) return;
    const info = getFailInfo(name);
    const now = Date.now();
    if (!info || !isSameDay(info.firstTime, now)) {
      localStorage.setItem(
        failUserName(name),
        JSON.stringify({ count: 1, firstTime: now })
      );
    } else {
      localStorage.setItem(
        failUserName(name),
        JSON.stringify({
          count: (info.count || 0) + 1,
          firstTime: info.firstTime,
        })
      );
    }
  };
  const clearFail = (name) => {
    if (!name) return;
    localStorage.removeItem(failUserName(name));
  };

  // 处理登录
  const handleLogin = () => {
    if (!agreementChecked) {
      message.warning("请先阅读并同意用户协议");
      setShake(true);
      return;
    }
    setLoading(true);
    // 获取人机验证的值
    let luotestResponse = document.querySelector(
      'input[name="luotest_response"]'
    )?.value;

    if (activeTab === "phone") {
      // 手机号登录逻辑
      login_by_mobile({
        mobile: phoneNumber,
        roaming_code: "+86",
        sms_code: verificationCode,
      }).then(handleLoginResponse);
    } else {
      // 账号登录逻辑
      const loginParams = {
        user_name: username,
        password: password,
        vcode: "",
      };
      if (needCaptcha) {
        if (!luotestResponse || luotestResponse.length === 0) {
          message.warning("请先完成验证码验证");
          setLoading(false);
          return;
        }
        loginParams.vcode = luotestResponse;
      }
      do_login(loginParams).then(handleLoginResponse);
    }
  };

  const handleLoginResponse = (res) => {
    setLoading(false);
    if (res.data.status === 200) {
      const { sid, uname } = res.data?.data;
      setCookie("sid", sid, 7);
      setCookie("uname", uname, 7);
      onSuccess();
    } else {
      if (res.data.status === 403) {
        setPunishmentModalVisible(true);
        setPunishmentContent(res.data?.data?.msg);
        setPunishmentTime(res.data?.data?.end_time);
      } else {
        failCount(username);
        setNeedCaptcha(shouldRequireCaptcha(username));
        message.warning(res.data.msg);
        // 登录失败时，如果是账号登录模式，重置验证码
        if (activeTab === "account" && needCaptcha) {
          resetCaptcha();
        }
      }
    }
  };

  // 第三方登录
  const handleThirdPartyLogin = (type) => {
    // if (type === "weixin") {
    //   window.location = "/new/auth/wxLogin";
    // } else {
    window.location = `https://passport.kdslife.com/index.php?c=login&m=union_${type}_login`;
    // }
  };

  if (!visible) return null;

  return (
    <>
      <div className="login-modal">
        <div className="login-modal__overlay"></div>
        <div className="login-modal__content">
          <div className="login-modal__close" onClick={onClose}>
            <img
              src={images[theme === "light" ? "close.svg" : "close.svg"]}
              alt=""
            />
          </div>

          <div className="login-modal__tabs">
            <div
              className={`login-modal__tab ${
                activeTab === "account" ? "login-modal__tab--active" : ""
              }`}
              onClick={() => setActiveTab("account")}
            >
              <span className="sys-article-title">账号登录</span>
              <div className="border-bottom"></div>
            </div>
            <div
              className={`login-modal__tab ${
                activeTab === "phone" ? "login-modal__tab--active" : ""
              }`}
              onClick={() => setActiveTab("phone")}
            >
              <span className="sys-article-title">手机号登录</span>
              <div className="border-bottom"></div>
            </div>
          </div>
          <Form form={form} onFinish={handleLogin}>
            <div className="login-modal__form">
              {activeTab === "account" ? (
                <>
                  <Form.Item
                    name="username"
                    rules={[{ required: true, message: "请输入账号" }]}
                  >
                    <Input
                      onChange={(e) => setUsername(e.target.value)}
                      className="login-modal__input sys-article-body"
                      prefix={
                        <img
                          src={
                            images[
                              theme === "light" ? "user.svg" : "user_d.svg"
                            ]
                          }
                          alt="user"
                        />
                      }
                      placeholder="请输入账号"
                    />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    rules={[{ required: true, message: "请输入密码" }]}
                  >
                    <Input.Password
                      onChange={(e) => setPassword(e.target.value)}
                      className="login-modal__input sys-article-body"
                      prefix={
                        <img
                          src={
                            images[
                              theme === "light"
                                ? "password.svg"
                                : "password_d.svg"
                            ]
                          }
                          alt="password"
                        />
                      }
                      placeholder="请输入密码"
                    />
                  </Form.Item>
                  {/* 新增验证码组件 */}
                  {needCaptcha && (
                    <div className="login-modal__input-group">
                      <div
                        className="l-captcha"
                        data-site-key="1b742a0db9bfa95b0b60e34a637c9914"
                        data-width="100%"
                        data-callback="luosimao_captcha_verify"
                      ></div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Form.Item
                    name="phone"
                    rules={[
                      {
                        pattern: /^1[3-9]\d{9}$/,
                        message: "请输入有效的手机号码",
                      },
                    ]}
                  >
                    <Input
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="login-modal__input sys-article-body"
                      prefix={
                        <>
                          <img
                            src={
                              images[
                                theme === "light" ? "phone.svg" : "phone_d.svg"
                              ]
                            }
                            alt="phone"
                          />
                          <span>+86</span>
                        </>
                      }
                      placeholder="请输入手机号码"
                      maxLength={11}
                    />
                  </Form.Item>
                  <Form.Item
                    name="code"
                    rules={[
                      { required: true, message: "请输入验证码" },
                      {
                        min: 6,
                        max: 6,
                        message: "验证码必须为6位数字",
                        validateTrigger: ["onChange"],
                      },
                    ]}
                  >
                    <Input
                      onChange={(e) => setVerificationCode(e.target.value)}
                      className="login-modal__input sys-article-body"
                      prefix={
                        <img
                          src={
                            images[
                              theme === "light" ? "code.svg" : "code_d.svg"
                            ]
                          }
                          alt="code"
                        />
                      }
                      placeholder="请输入短信验证码"
                      maxLength={6}
                      suffix={
                        <span
                          className={`sys-regular-bold-font code-btn ${
                            countdown > 0 ? "code-btn--disabled" : ""
                          }`}
                          onClick={countdown === 0 ? handleSendCode : null}
                        >
                          {countdown > 0
                            ? `获取验证码(${countdown}s)`
                            : "获取验证码"}
                        </span>
                      }
                    />
                  </Form.Item>
                </>
              )}
              <Form.Item>
                <div
                  onClick={handleLogin}
                  className={`sys-editor-body ${
                    loading ? "submit-btn-disableds" : "login-modal__submit-btn"
                  }`}
                >
                  {loading ? "登录中..." : "登录"}
                </div>
              </Form.Item>
              <div
                className={`login-modal__agreement sys-regular-font ${
                  shake ? "shake" : ""
                }`}
                onAnimationEnd={() => setShake(false)}
              >
                <Checkbox
                  checked={agreementChecked}
                  onChange={(e) => setAgreementChecked(e.target.checked)}
                >
                  我已阅读并同意{" "}
                  <a
                    href="https://club.kdslife.com/new/terms/serve.html"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    《用户服务协议》
                  </a>
                  <a
                    href="https://club.kdslife.com/new/terms/privacy.html"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    《隐私政策》
                  </a>
                </Checkbox>
              </div>
            </div>
          </Form>
          <div className="login-modal__other-methods">
            <div className="login-modal__social-icons">
              {/* <div
                className="login-modal__social-icon"
                onClick={() => handleThirdPartyLogin("weibo")}
              >
                <img
                  src={images[theme === "light" ? "weibo.svg" : "weibo_d.svg"]}
                  alt="微博登录"
                />
              </div> */}
              <div
                className="login-modal__social-icon"
                onClick={() => handleThirdPartyLogin("weixin")}
              >
                <img
                  src={
                    images[theme === "light" ? "wechat.svg" : "wechat_d.svg"]
                  }
                  alt="微信登录"
                />
              </div>
              {/* <div
                className="login-modal__social-icon"
                onClick={() => handleThirdPartyLogin("qq")}
              >
                <img
                  src={images[theme === "light" ? "qq.svg" : "qq_d.svg"]}
                  alt="QQ登录"
                />
              </div> */}
            </div>
            <div className="login-modal__other-title sys-regular-font">
              其他方式
            </div>
          </div>

          <div className="login-modal__footer sys-regular-font">
            <div className="footer-left">
              <span className="login-modal__footer-link">
                <span
                  onClick={() =>
                    window.open("/new/auth/forgotAccount", "_blank")
                  }
                  // className="nav-link"
                >
                  找回账号
                </span>
              </span>
              <span>|</span>
              <span className="login-modal__footer-link">
                <span
                  onClick={() =>
                    window.open("/new/auth/forgotPassword", "_blank")
                  }
                  // className="nav-link"
                >
                  找回密码
                </span>
              </span>
            </div>
            <span className="login-modal__footer-link">
              <span
                onClick={() => window.open("/new/auth/register", "_blank")}
                // className="nav-link"
              >
                手机注册
              </span>
            </span>
          </div>
        </div>
      </div>
       {/* 对用户的处罚弹窗 */}
      <ConfirmModal
        visible={punishmentModalVisible}
        onConfirm={() => {
          setPunishmentModalVisible(false);
        }}
        showCancelButton={false}
        imageUrl={images["warning.svg"]}
        confirmText="好的"
        width={320}
        message={`<p>您的权限因不当言论被暂时限制，解封时间<span style="color: green"> [${formatDateByYearFull(
          punishmentTime
        )} ]</span></p>
     <p>建议阅读<a style="color: var(--primary-color)" href="https://club.kdslife.com/new/terms/kds-com.html" rel="noopener noreferrer" target="_blank" >《社区行为规范》</a>确保内容合规，感谢您对社区建设的理解与支持。</p>`}
      />
    </>
  );
};

export default LoginModal;
